<template>
  <div class="pa-form">
    <div class="pa-header">
      <div class="pa-header-left">
        <span class="pa-icon"><font-awesome-icon :icon="['fas', 'file-signature']" /></span>
        <span class="pa-title">{{ isCreate ? "New Permit Application" : "Edit Permit Application" }}</span>
      </div>
      <button class="pa-close" type="button" :disabled="saving" aria-label="Close" @click="$emit('close')">
        <font-awesome-icon :icon="['fas', 'times']" />
      </button>
    </div>

    <div class="pa-body">
      <p class="pa-hint">
        Writes the fields on one Permit Application node — the milestone record
        (RZ / DP / BP …) referenced by a project, its type collection or a unit
        type. The presale project itself is edited on the project page.
      </p>

      <!-- Opened from a project, the form can add a milestone to it as well as
           edit one that already exists. Without a project it only edits. -->
      <div v-if="presaleId" class="pa-modes">
        <button type="button" class="pa-mode" :class="{ 'pa-mode--active': isCreate }" :disabled="saving"
          @click="setMode('create')">Add to this project</button>
        <button type="button" class="pa-mode" :class="{ 'pa-mode--active': !isCreate }" :disabled="saving"
          @click="setMode('edit')">Find existing</button>
      </div>

      <!-- Step 0 of a create: the level the milestone hangs off, shown before
           any field is. Every caller has a button per level -- the project
           header files against the project, a building's header against that
           building, a component column against that component -- so this states
           the level it was opened at rather than re-asking a question the
           button already answered. What it still asks is WHICH building or
           component, which the button cannot say on its own. -->
      <template v-if="levelStep">
        <div class="pa-row">
          <span class="pa-label">What is this application for?</span>
          <button v-for="choice in levelChoices" :key="choice.key" type="button" class="pa-level"
            :class="{ 'pa-level--active': pendingPlacement === choice.key }" :disabled="choice.disabled || saving"
            @click="choosePlacement(choice.key)">
            <span class="pa-level-title">{{ choice.title }}</span>
            <span class="pa-level-note">{{ choice.disabled ? choice.unavailable : choice.note }}</span>
          </button>
        </div>

        <div v-if="pendingPlacement === 'ptc'" class="pa-row">
          <label class="pa-label" for="pa-level-ptc">Which building</label>
          <select id="pa-level-ptc" class="pa-input" :value="pendingFid"
            @change="pendingFid = $event.target.value">
            <option v-for="ptc in ptcOptions" :key="ptc.fid" :value="ptc.fid">
              {{ ptc.name || `type collection ${ptc.fid}` }}
            </option>
          </select>
        </div>

        <div v-if="pendingPlacement === 'utc'" class="pa-row">
          <label class="pa-label" for="pa-level-utc">Which component</label>
          <select id="pa-level-utc" class="pa-input" :value="pendingUid"
            @change="pendingUid = $event.target.value">
            <option v-for="utc in utcOptions" :key="utc.uid" :value="utc.uid">
              {{ utcLabel(utc) }}
            </option>
          </select>
        </div>

        <p class="pa-help">
          Pick where the application is saved. The placement selector on the next
          screen can still move it before saving.
        </p>
      </template>

      <div v-if="isCreate && !levelStep" class="pa-current">
        <div class="pa-current-title">{{ presaleTitle || `Presale project ${presaleId}` }}</div>
        <dl class="pa-current-grid">
          <dt>Project</dt>
          <dd>{{ presaleId }}</dd>
          <dt>Referenced from</dt>
          <dd>{{ targetLabel }}</dd>
        </dl>
        <p class="pa-help">
          Saving creates the node and adds it to the project's Permit
          Applications. A permit number the project already carries updates that
          milestone instead of adding a second one.
        </p>
      </div>

      <!-- Step 1: which application. Nothing else is enabled until one is
           loaded, so a value can never be written against a mistyped ID. -->
      <div v-if="!isCreate" class="pa-row pa-row--load">
        <label class="pa-label" for="pa-ref">Application ID, permit # or title</label>
        <div class="pa-load">
          <input id="pa-ref" v-model="reference" class="pa-input" type="text"
            placeholder="e.g. 1382895, DP-2026-00056 or PRJ17-013" :disabled="loading || saving"
            @keyup.enter="loadApplication" />
          <button type="button" class="pa-load-btn" :disabled="!reference || loading || saving" @click="loadApplication">
            <font-awesome-icon v-if="loading" :icon="['fas', 'spinner']" class="fa-spin" />
            {{ loading ? "Loading…" : "Load" }}
          </button>
        </div>
        <p class="pa-help">
          Digits load a node ID directly. Anything else is searched against both
          the title and the permit number — a project number such as PRJ17-013 is
          carried by every one of its milestone nodes, so pick the right one from
          the matches.
        </p>
      </div>

      <!-- Search hits. Shown instead of jumping to the newest match, because the
           milestones of one project are only told apart by stage and date. -->
      <div v-if="matches.length" class="pa-row">
        <span class="pa-label">{{ matches.length }} match{{ matches.length === 1 ? "" : "es" }}</span>
        <ul class="pa-matches">
          <li v-for="match in matches" :key="match.nid">
            <button type="button" class="pa-match" :disabled="loading || saving" @click="selectMatch(match.nid)">
              <span class="pa-match-title">{{ match.title || `Application ${match.nid}` }}</span>
              <span class="pa-match-meta">
                {{ [match.permit, match.stage, match.date_iso].filter(Boolean).join(" · ") || "no permit details" }}
              </span>
              <span class="pa-match-meta">{{ match.address || "no address" }} · node {{ match.nid }}</span>
            </button>
          </li>
        </ul>
        <p v-if="moreMatches" class="pa-help">
          Only the first {{ matches.length }} are listed — narrow the search to see the rest.
        </p>
      </div>

      <div v-if="error" class="pa-alert pa-alert--error">
        <font-awesome-icon :icon="['fas', 'exclamation-circle']" />
        <span>{{ error }}</span>
      </div>

      <template v-if="showFields">
        <!-- Stored values, so the operator can see what is being replaced. -->
        <div v-if="loaded" class="pa-current">
          <div class="pa-current-title">{{ loaded.title || `Application ${loaded.nid}` }}</div>
          <dl class="pa-current-grid">
            <dt>Node</dt>
            <dd>{{ loaded.nid }}</dd>
            <dt>Stage</dt>
            <dd>{{ storedStageNames || "—" }}</dd>
            <dt>Applied</dt>
            <dd>{{ loaded.field_applicateion_date_iso || "—" }}</dd>
            <dt>Address</dt>
            <dd>{{ loaded.field_associated_address || "—" }}</dd>
          </dl>
        </div>

        <!-- Which entity carries the reference. The convention this module
             documents (planning_api.inc): Rezoning / DP / OCP amendment hang off
             the presale node, a Building Permit off the type collection. -->
        <div v-if="isCreate && (ptcOptions.length || utcOptions.length)" class="pa-row">
          <label class="pa-label" for="pa-placement">File this milestone against</label>
          <select id="pa-placement" class="pa-input" :disabled="saving" :value="targetKey"
            @change="setTarget($event.target.value)">
            <option value="main">This project — Rezoning / Development / OCP amendment</option>
            <optgroup v-if="ptcOptions.length" label="Building (type collection)">
              <option v-for="ptc in ptcOptions" :key="ptc.fid" :value="'ptc:' + ptc.fid">
                Building Permit — {{ ptc.name || `type collection ${ptc.fid}` }}
              </option>
            </optgroup>
            <optgroup v-if="utcOptions.length" label="Component (unit type collection)">
              <option v-for="utc in utcOptions" :key="utc.uid" :value="'utc:' + utc.uid">
                {{ utcLabel(utc) }}
              </option>
            </optgroup>
          </select>
          <p class="pa-help">
            A Rezoning, Development or OCP application belongs to the project; a
            Building Permit belongs to one building's type collection; a
            milestone covering a single component belongs to that component's
            unit type collection. Filing it at the wrong level hides it from
            that level's permit status.
          </p>
          <p v-if="buildingStagePicked && targetKey === 'main'" class="pa-help pa-help--warn">
            The stage you ticked looks like a Building Permit, but this is filed
            against the project. Pick the building above if that was not intended.
          </p>
        </div>

        <div class="pa-row pa-row--pair">
          <div>
            <label class="pa-label" for="pa-title">Title</label>
            <input id="pa-title" v-model="form.title" class="pa-input" type="text" :disabled="saving" />
          </div>
          <div>
            <label class="pa-label" for="pa-date">Application date</label>
            <input id="pa-date" v-model="form.applicationDate" class="pa-input" type="date" :disabled="saving" />
          </div>
        </div>
        <p v-if="!isCreate && !form.title.trim()" class="pa-help">
          A node cannot be saved without a title — the stored one is kept while
          this box is empty.
        </p>

        <!-- Unlimited-cardinality text fields: Drupal offers "Add another item"
             for each, so a single box would hide values 2..N. -->
        <div v-for="field in multiTextFields" :key="field.key" class="pa-row">
          <label class="pa-label" :for="'pa-' + field.key + '-0'">{{ field.label }}</label>
          <div v-for="(value, idx) in form[field.key]" :key="field.key + '-' + idx" class="pa-multi-item">
            <textarea v-if="field.type === 'textarea'" :id="'pa-' + field.key + '-' + idx" class="pa-input pa-textarea"
              rows="4" :value="value" :disabled="saving"
              @input="setValue(field.key, idx, $event.target.value)"></textarea>
            <input v-else :id="'pa-' + field.key + '-' + idx" class="pa-input" type="text" :value="value"
              :placeholder="field.placeholder" :disabled="saving"
              @input="setValue(field.key, idx, $event.target.value)" />
            <button v-if="form[field.key].length > 1" type="button" class="pa-multi-remove" :disabled="saving"
              :aria-label="'Remove ' + field.label + ' value ' + (idx + 1)" @click="removeValue(field.key, idx)">
              <font-awesome-icon :icon="['fas', 'times']" />
            </button>
          </div>
          <button type="button" class="pa-multi-add" :disabled="saving" @click="addValue(field.key)">
            + Add another item
          </button>
        </div>

        <div class="pa-row">
          <span class="pa-label">City Based Application Stage <span class="pa-required">*</span></span>

          <!-- One row per stored value, mirroring the node form's widget: a
               City -> Permit Type -> Stage cascade that stores the LEAF, so
               each row walks down a level at a time. The vocabulary is the
               static tree in data/cityApplicationStages.js, so every level
               opens without a request. -->
          <div v-for="(row, rowIdx) in stageRows" :key="'stage-row-' + rowIdx" class="pa-stage-row">
            <select v-for="(level, depth) in row.levels" :key="'level-' + depth" class="pa-input pa-cascade-select"
              :disabled="saving" :value="level.selected"
              @change="pickStageLevel(rowIdx, depth, $event.target.value)">
              <option value="">{{ depth === 0 ? "- Please select -" : "- select -" }}</option>
              <option v-for="option in level.options" :key="option.tid" :value="option.tid">
                {{ option.name }}{{ option.has_children ? " ›" : "" }}
              </option>
            </select>
            <button type="button" class="pa-multi-remove pa-stage-drop" :disabled="saving"
              :aria-label="'Remove stage row ' + (rowIdx + 1)" @click="removeStageRow(rowIdx)">
              <font-awesome-icon :icon="['fas', 'times']" />
            </button>
            <span v-if="rowIncomplete(row)" class="pa-stage-flag">keep going</span>
          </div>

          <button type="button" class="pa-multi-add" :disabled="saving" @click="addStageRow">
            + Add another item
          </button>

          <p v-if="stageError" class="pa-help pa-help--warn">{{ stageError }}</p>
          <p v-if="stageMissing && !isCreate" class="pa-help pa-help--warn">
            This field is required - add a stage back before saving, or cancel to
            leave the stored one alone.
          </p>
          <p v-else-if="incompleteRows" class="pa-help pa-help--warn">
            {{ incompleteRows }} {{ incompleteRows === 1 ? "row is" : "rows are" }}
            unfinished - a term with children is not a valid value, so keep picking until
            the last box is a leaf. Unfinished rows are not saved.
          </p>
          <p class="pa-help">
            Multi-valued: add a row per stage the milestone carries. Only the
            last level of a branch is a valid value.
          </p>

          <!-- CBA Status, read-only. The statuses hang off the stage TERM, not
               off this node, and that term is shared by every application that
               picks the stage -- editing one here would move the status for
               every other project on it. So this reflects what is STORED: it
               does not follow the cascades above until the form is saved and
               reloaded, and a create has nothing to show yet. -->
          <template v-if="storedStageCba.length">
            <span class="pa-label pa-label--sub">CBA Status</span>
            <ul class="pa-cba-list">
              <li v-for="(entry, entryIdx) in storedStageCba" :key="'cba-' + entryIdx + '-' + entry.tid"
                class="pa-cba-item">
                <span class="pa-cba-stage">{{ entry.label }}</span>
                <span class="pa-cba-chips">
                  <span v-for="item in entry.cba" :key="entryIdx + '-' + item.code" class="pt-cba"
                    :class="cbaClass(item.status)">
                    <strong>{{ item.code }}</strong> {{ item.status }}
                  </span>
                </span>
              </li>
            </ul>
            <p class="pa-help">
              Carried by the stage itself and shared by every application that
              picks it, so it is read-only here and shows the saved stage rather
              than a pick that has not been saved yet. Edit it on the term in
              Drupal to change it everywhere.
            </p>
          </template>
        </div>

        <!-- field_planning_documents_type_co: unlimited cardinality, so the
             whole set is edited here and written as one value on save. Files
             are uploaded as they are picked, but only referenced by the node
             once the form is saved. -->
        <div class="pa-row">
          <span class="pa-label">Planning documents</span>

          <ul v-if="attachedFiles.length" class="pa-files">
            <li v-for="(file, idx) in attachedFiles" :key="file.fid">
              <a v-if="file.url" :href="file.url" target="_blank" rel="noopener">
                {{ file.filename || `File ${file.fid}` }}
              </a>
              <span v-else>{{ file.filename || `File ${file.fid}` }}</span>
              <button type="button" class="pa-multi-remove" :disabled="saving || uploadingFiles"
                :aria-label="'Remove ' + (file.filename || 'file ' + file.fid)" @click="removeFile(idx)">
                <font-awesome-icon :icon="['fas', 'times']" />
              </button>
            </li>
          </ul>

          <label class="pa-multi-add pa-file-add" :class="{ 'pa-file-add--busy': uploadingFiles || saving }">
            <font-awesome-icon v-if="uploadingFiles" :icon="['fas', 'spinner']" class="fa-spin" />
            {{ uploadingFiles ? "Uploading…" : "+ Add another item" }}
            <input ref="fileInput" class="pa-file-input" type="file" multiple :disabled="saving || uploadingFiles"
              @change="onFilesPicked" />
          </label>

          <p v-if="fileError" class="pa-help pa-help--warn">{{ fileError }}</p>
          <p class="pa-help">
            Pick several at once, or add them a batch at a time. Removing one
            here detaches it from this application on the next save — the file
            itself stays on the server.
          </p>
        </div>

        <div v-if="warnings.length" class="pa-alert pa-alert--warn">
          <font-awesome-icon :icon="['fas', 'exclamation-circle']" />
          <div>
            <div class="pa-alert-title">Saved with warnings</div>
            <div v-for="(warning, index) in warnings" :key="'w-' + index">{{ warning }}</div>
          </div>
        </div>
        <div v-else-if="notice" class="pa-alert pa-alert--ok">
          <font-awesome-icon :icon="['fas', 'check-circle']" />
          <span>{{ notice }}</span>
        </div>
      </template>
    </div>

    <div class="pa-footer">
      <div class="pa-footer-status">
        <template v-if="levelStep">Choose where this application is filed</template>
        <template v-else-if="isCreate && !changedFields.length">Fill in the permit details</template>
        <template v-else-if="stageMissing">A City Based Application Stage is required</template>
        <template v-else-if="isCreate && !canSave">A title or permit # is needed</template>
        <template v-else-if="!showFields">Load an application to begin</template>
        <template v-else-if="changedFields.length">
          {{ changedFields.length }} {{ changedFields.length === 1 ? "field" : "fields" }} to write
        </template>
        <template v-else>No changes yet</template>
      </div>
      <div class="pa-footer-actions">
        <button type="button" class="pa-cancel-btn" :disabled="saving" @click="$emit('close')">Cancel</button>
        <button v-if="levelStep" type="button" class="pa-save-btn" :disabled="!canPickLevel"
          @click="pickLevel">Continue</button>
        <button v-else type="button" class="pa-save-btn" :disabled="!canSave" @click="save">
          <font-awesome-icon v-if="saving" :icon="['fas', 'spinner']" class="fa-spin" />
          {{ saving ? "Saving…" : isCreate ? "Create application" : "Save changes" }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { useProjectStore } from "../../store/ProjectStore";
import { stageBreadcrumb, stageLevelOptions, stageLevelsFor } from "../../data/cityApplicationStages";
import { cbaStatusClass } from "../../utilities/cbaStatus";

// The three unlimited-cardinality text fields are arrays here, one entry per
// Drupal delta; a lone "" is the empty row the form always shows.
const MULTI_FIELDS = [
  { key: "field_folder_permit_", label: "Folder / Permit #", placeholder: "e.g. DP-2026-00056", type: "text" },
  { key: "field_associated_address", label: "Associated address", placeholder: "e.g. 8460 Ash Street", type: "text" },
  { key: "field_project_description_permit", label: "Project description", placeholder: "", type: "textarea" },
  { key: "field_planning_documents_link", label: "Planning documents link", placeholder: "https://…", type: "text" },
];

const emptyForm = () => ({
  title: "",
  applicationDate: "",
  // Attachments as [{ fid, filename, url }] — the stored ones plus anything
  // uploaded in this session. Written as the fid list on save.
  files: [],
  field_folder_permit_: [""],
  field_associated_address: [""],
  field_project_description_permit: [""],
  field_planning_documents_link: [""],
});

export default {
  name: "PermitApplicationForm",
  props: {
    // When set, the form can also CREATE a Permit Application under this
    // presale project (its node id) — not just edit one that exists.
    presaleId: { type: [Number, String], default: null },
    presaleTitle: { type: String, default: "" },
    // Where a created application is referenced from: the project node
    // ("main"), one of the project's type collections ("ptc"), or one of their
    // unit type collections ("utc").
    placement: { type: String, default: "main" },
    // Which collection the "ptc" / "utc" placement means. A caller that opens
    // the form from one building or one component names it here; left empty,
    // the placement falls back to the first option, as it always did.
    placementFid: { type: [Number, String], default: null },
    placementUid: { type: [Number, String], default: null },
    // Open straight onto this application (node id or permit number) in edit
    // mode — what the Land tab's per-milestone Edit passes.
    applicationRef: { type: [Number, String], default: null },
    // The project's type collections, [{ fid, name }], so a Building Permit can
    // be filed against the building it belongs to.
    ptcOptions: { type: Array, default: () => [] },
    // Every unit type collection under those, [{ uid, fid, name, ptcName }], so
    // a milestone covering one component can be filed against it. `uid` is the
    // field collection item id api/permit_application/save takes.
    utcOptions: { type: Array, default: () => [] },
  },
  data() {
    return {
      // "create" writes a new node under presaleId; "edit" loads an existing
      // one. Opened from a project the useful default is adding to it, unless a
      // specific application was named.
      mode: this.presaleId && !this.applicationRef ? "create" : "edit",
      // A create starts on the level step; an edit knows where its milestone
      // already hangs, so there is nothing to ask.
      levelPicked: !(this.presaleId && !this.applicationRef),
      // What that step holds until it is confirmed. Kept apart from `targetKey`
      // so backing out of the step cannot half-apply a placement.
      pendingPlacement: "main",
      pendingFid: "",
      pendingUid: "",
      // Only ever set when a stored stage cannot be found in the static tree.
      stageError: "",
      uploadingFiles: false,
      fileError: "",
      // "main", "ptc:<fid>" or "utc:<uid>" — what the placement selector holds.
      targetKey: "main",
      reference: "",
      matches: [],
      moreMatches: false,
      loaded: null,
      // One row per value, mirroring the node form's widget. Each row is
      // { levels: [{ options: [...], selected: tid|"" }] }, root first.
      stageRows: [],
      // tid -> "City / Permit Type / Stage", for labelling picked leaves.
      stageLabels: {},
      loading: false,
      saving: false,
      error: null,
      notice: "",
      warnings: [],
      form: emptyForm(),
    };
  },
  computed: {
    projectStore() {
      return useProjectStore();
    },
    isCreate() {
      return this.mode === "create";
    },
    // On the level step: a create that has not been told where the milestone
    // belongs yet. Nothing below it renders until it is answered.
    levelStep() {
      return this.isCreate && !this.levelPicked;
    },
    // The three places an application can be saved: the presale project, one
    // building (PTC) or one component (UTC). A level with nothing to file
    // against (no buildings / components passed in) is shown but disabled.
    levelChoices() {
      return [
        {
          key: "main",
          title: "Presale project",
          note: "Rezoning, Development Permit, OCP amendment — anything filed for the development as a whole. Stored on the project node.",
          disabled: false,
        },
        {
          key: "ptc",
          title: "One building (PTC)",
          note: "A Building Permit belongs to the building it is pulled for, not to the project.",
          unavailable: "This project has no building (type collection) to file against.",
          disabled: !this.ptcOptions.length,
        },
        {
          key: "utc",
          title: "One component (UTC)",
          note: "A milestone covering one component of a building — the rental tower over a strata podium, the retail base — rather than the building as a whole.",
          unavailable: "This project has no component (unit type collection) to file against.",
          disabled: !this.utcOptions.length,
        },
      ];
    },
    canPickLevel() {
      if (this.saving) return false;
      if (this.pendingPlacement === "ptc") return Boolean(this.pendingFid);
      if (this.pendingPlacement === "utc") return Boolean(this.pendingUid);
      return true;
    },
    // The top level (cities), shared by every row. Static, so there is no
    // loading state and no empty-vocabulary case to render around.
    rootOptions() {
      return stageLevelOptions();
    },
    multiTextFields() {
      return MULTI_FIELDS;
    },
    targetPlacement() {
      if (this.targetKey.indexOf("ptc:") === 0) return "ptc";
      if (this.targetKey.indexOf("utc:") === 0) return "utc";
      return "main";
    },
    targetFid() {
      return this.targetPlacement === "ptc" ? this.targetKey.slice(4) : null;
    },
    targetUid() {
      return this.targetPlacement === "utc" ? this.targetKey.slice(4) : null;
    },
    // The chosen unit type collection, or null at any other level. Read by the
    // label and by the save, so the two cannot disagree about which component.
    targetUtc() {
      if (!this.targetUid) return null;
      return this.utcOptions.find((option) => String(option.uid) === String(this.targetUid)) || null;
    },
    targetLabel() {
      if (this.targetPlacement === "ptc") {
        const ptc = this.ptcOptions.find((option) => String(option.fid) === String(this.targetFid));
        return `Building — ${(ptc && ptc.name) || this.targetFid}`;
      }
      if (this.targetPlacement === "utc") {
        return `Component — ${this.targetUtc ? this.utcLabel(this.targetUtc) : this.targetUid}`;
      }
      return "This project (project node)";
    },
    // True when a picked stage sits on a Building Permit branch — used only to
    // warn, never to switch the placement silently. The path carries the whole
    // City / Permit Type / Stage breadcrumb, so it is the thing to test.
    buildingStagePicked() {
      return this.stageRows.some((row) => /building\s*permit/i.test(this.rowPath(row)));
    },
    // The value of every finished row, deduped and in row order. A row whose
    // deepest pick still has children is not a value: the field stores leaves.
    pickedStages() {
      const tids = [];
      this.stageRows.forEach((row) => {
        const tid = this.rowLeafTid(row);
        if (tid && !tids.includes(tid)) tids.push(tid);
      });
      return tids;
    },
    // Rows the operator started but has not walked to a leaf. Counted so the
    // form can say so rather than dropping the row silently on save.
    incompleteRows() {
      return this.stageRows.filter((row) => this.rowIncomplete(row)).length;
    },
    // The field rows: in create mode once the level is settled, and when
    // editing only after a load.
    showFields() {
      if (this.levelStep) return false;
      return this.isCreate || Boolean(this.loaded);
    },
    storedFiles() {
      return (this.loaded && this.loaded.field_planning_documents_type_co) || [];
    },
    // The attachment rows. Read through this rather than form.files directly:
    // a hot-reloaded instance keeps the data object it was created with, so
    // `files` can be missing on a form that was already open when this field
    // was added, and the render must not die on it.
    attachedFiles() {
      return this.form.files || [];
    },
    // Attachment fids in row order. Order is a value here — it is the delta
    // order the node stores — so this is not sorted or deduped by set.
    pickedFileIds() {
      return this.attachedFiles.map((file) => Number(file.fid)).filter(Boolean);
    },
    // A new node needs something to be called; an edit needs a real change.
    // The stage is required on the node form, and the endpoint refuses both a
    // create without one and an update that empties it, so block here rather
    // than round-tripping for the error.
    stageMissing() {
      if (!this.pickedStages.length) {
        return this.isCreate || "field_city_based_application_sta" in this.payload;
      }
      return false;
    },
    canSave() {
      if (this.saving || !this.changedFields.length) return false;
      if (this.stageMissing) return false;
      if (!this.isCreate) return true;
      return Boolean(this.form.title.trim() || this.cleanValues("field_folder_permit_").length);
    },
    // The stage list as the backend sent it. Everything below reads through
    // this: it is the one place a payload without the key is turned into an
    // empty list, and all three readers run during render.
    storedStages() {
      const stages = this.loaded && this.loaded.field_city_based_application_sta;
      return Array.isArray(stages) ? stages : [];
    },
    storedStageNames() {
      return this.storedStages
        .map((stage) => stage.label || stage.name || stage.tid)
        .join(", ");
    },
    storedStageTids() {
      return this.storedStages.map((stage) => stage.tid);
    },
    // The read-only CBA statuses of the stored stages, one entry per stage that
    // carries any. `cba` comes off the stage term, so a payload that predates
    // the key simply renders nothing rather than an empty heading.
    storedStageCba() {
      return this.storedStages
        .map((stage) => ({
          tid: stage.tid,
          label: stage.label || stage.name || `Stage ${stage.tid}`,
          cba: (Array.isArray(stage.cba) ? stage.cba : []).filter((item) => item && item.status),
        }))
        .filter((entry) => entry.cba.length);
    },
    // The endpoint writes only the keys it is sent, so the payload is the diff
    // against what was loaded. An emptied box is a real value: "" clears the
    // field rather than meaning "leave it".
    payload() {
      const payload = {};
      // Nothing is stored yet, so every filled box is a value to write.
      if (this.isCreate) {
        const title = this.form.title.trim();
        if (title) payload.title = title;
        MULTI_FIELDS.forEach(({ key }) => {
          const values = this.cleanValues(key);
          if (values.length) payload[key] = values;
        });
        if (this.form.applicationDate) payload.field_applicateion_date = this.form.applicationDate;
        if (this.pickedStages.length) payload.field_city_based_application_sta = [...this.pickedStages];
        if (this.pickedFileIds.length) payload.field_planning_documents_type_co = [...this.pickedFileIds];
        return payload;
      }
      if (!this.loaded) return payload;

      const title = this.form.title.trim();
      if (title && title !== this.loaded.title) {
        payload.title = title;
      }

      // Whole-set replacement: send every remaining value, or "" to clear the
      // field. Untouched fields stay out of the payload, so a node carrying
      // more values than were edited cannot be collapsed by accident.
      MULTI_FIELDS.forEach(({ key }) => {
        const now = this.cleanValues(key);
        const was = this.loadedValues(key);
        if (now.join("\u0000") !== was.join("\u0000")) {
          payload[key] = now.length ? now : "";
        }
      });

      if (this.form.applicationDate !== (this.loaded.field_applicateion_date_iso || "")) {
        payload.field_applicateion_date = this.form.applicationDate;
      }

      const stored = [...this.storedStageTids].sort((a, b) => a - b).join(",");
      const picked = [...this.pickedStages].sort((a, b) => a - b).join(",");
      if (stored !== picked) {
        payload.field_city_based_application_sta = [...this.pickedStages];
      }

      // Whole-set replacement again, and order-sensitive: reordering the
      // attachments is a real change. "" drops the last one.
      const storedFids = this.storedFiles.map((file) => Number(file.fid)).join(",");
      if (storedFids !== this.pickedFileIds.join(",")) {
        payload.field_planning_documents_type_co = this.pickedFileIds.length ? [...this.pickedFileIds] : "";
      }

      return payload;
    },
    changedFields() {
      return Object.keys(this.payload);
    },
  },
  created() {
    // The prop is the caller's intent; resolve "ptc" to a concrete fid so the
    // selector and the request agree on which building.
    if (this.placement === "ptc" && this.ptcOptions.length) {
      const named = this.ptcOptions.find((option) => String(option.fid) === String(this.placementFid));
      this.targetKey = `ptc:${(named || this.ptcOptions[0]).fid}`;
    }
    else if (this.placement === "utc" && this.utcOptions.length) {
      const named = this.utcOptions.find((option) => String(option.uid) === String(this.placementUid));
      this.targetKey = `utc:${(named || this.utcOptions[0]).uid}`;
    }
    // The level step opens on whatever the caller asked for, so confirming it
    // without touching anything keeps the old behaviour.
    this.pendingPlacement = this.targetPlacement;
    this.pendingFid = this.targetFid
      || (this.ptcOptions.length ? String(this.ptcOptions[0].fid) : "");
    this.pendingUid = this.targetUid
      || (this.utcOptions.length ? String(this.utcOptions[0].uid) : "");
  },
  mounted() {
    // The cities are static, so the picker is usable from the first paint —
    // only the blank row it starts in has to be put there.
    this.ensureBlankRow();
    if (this.applicationRef) {
      this.reference = String(this.applicationRef);
      this.loadApplication();
    }
  },
  methods: {
    setMode(mode) {
      if (this.mode === mode || this.saving) return;
      this.mode = mode;
      // Switching back to a create is a new milestone, so it asks again.
      this.levelPicked = mode !== "create";
      this.reference = "";
      this.resetResult();
    },
    // Selects one of the three levels on the level step. Nothing is written
    // until pickLevel() confirms it.
    choosePlacement(key) {
      const choice = this.levelChoices.find((c) => c.key === key);
      if (!choice || choice.disabled || this.saving) return;
      this.pendingPlacement = key;
      if (key === "ptc" && !this.pendingFid && this.ptcOptions.length) {
        this.pendingFid = String(this.ptcOptions[0].fid);
      }
      if (key === "utc" && !this.pendingUid && this.utcOptions.length) {
        this.pendingUid = String(this.utcOptions[0].uid);
      }
    },
    // Confirms the level step. This is the only place the step's answer reaches
    // `targetKey`, which is what the request is built from.
    pickLevel() {
      if (!this.canPickLevel) return;
      if (this.pendingPlacement === "ptc" && this.pendingFid) {
        this.targetKey = `ptc:${this.pendingFid}`;
      }
      else if (this.pendingPlacement === "utc" && this.pendingUid) {
        this.targetKey = `utc:${this.pendingUid}`;
      }
      else {
        this.targetKey = "main";
      }
      this.levelPicked = true;
    },
    // "Component name — building name", the only reading that identifies a UTC:
    // its own name is routinely the unit type alone, which repeats across the
    // buildings of one project.
    utcLabel(utc) {
      const name = utc.name || `unit type collection ${utc.uid}`;
      return utc.ptcName ? `${name} — ${utc.ptcName}` : name;
    },
    // A row the operator can start picking in: just the city level.
    blankRow() {
      return { levels: [{ options: this.rootOptions, selected: "" }] };
    },
    // The node form always leaves one empty row to add into; match it.
    ensureBlankRow() {
      const trailing = this.stageRows[this.stageRows.length - 1];
      if (trailing && this.rowLevels(trailing)[0] && !this.rowLevels(trailing)[0].selected) return;
      this.stageRows.push(this.blankRow());
    },
    addStageRow() {
      this.stageRows.push(this.blankRow());
    },
    // Removing the last row leaves a blank one behind: with no rows at all
    // there is nothing to pick in, which is the dead end this form had.
    removeStageRow(rowIdx) {
      this.stageRows.splice(rowIdx, 1);
      this.ensureBlankRow();
    },
    // Choosing at one level of one row discards the levels below it in that
    // row, then opens the next level when the chosen term has children.
    pickStageLevel(rowIdx, depth, value) {
      const row = this.stageRows[rowIdx];
      const levels = this.rowLevels(row);
      if (!levels[depth]) return;
      const level = levels[depth];
      this.$set(level, "selected", value);
      this.$set(row, "levels", levels.slice(0, depth + 1));
      if (!value) return;
      const option = level.options.find((o) => String(o.tid) === String(value));
      if (!option) return;
      // A leaf completes the row, so offer the next one the way the node form
      // does rather than making the operator hunt for the add button.
      if (!option.has_children) {
        this.$set(this.stageLabels, option.tid, this.rowPath(row));
        this.ensureBlankRow();
        return;
      }
      row.levels.push({ options: stageLevelOptions(option.tid), selected: "" });
    },
    // The cascade of one row. Rows are always built with levels, but they are
    // read from computeds that run on every render, so a malformed one is worth
    // rendering as an empty row rather than throwing out of render.
    rowLevels(row) {
      return (row && row.levels) || [];
    },
    // The deepest pick in a row, but only when it is a leaf: a term with
    // children is not a valid value for this field.
    rowLeafTid(row) {
      const levels = this.rowLevels(row);
      for (let depth = levels.length - 1; depth >= 0; depth -= 1) {
        const level = levels[depth];
        if (!level.selected) continue;
        const option = level.options.find((o) => String(o.tid) === String(level.selected));
        if (!option) return null;
        return option.has_children ? null : Number(option.tid);
      }
      return null;
    },
    // Started but not finished: something is picked, yet not down to a leaf.
    rowIncomplete(row) {
      const started = this.rowLevels(row).some((level) => level.selected);
      return started && !this.rowLeafTid(row);
    },
    // "City / Permit Type / Stage" for whatever the row has picked so far.
    rowPath(row) {
      const names = [];
      this.rowLevels(row).forEach((level) => {
        if (!level.selected) return;
        const option = level.options.find((o) => String(o.tid) === String(level.selected));
        if (option) names.push(option.name);
      });
      return names.join(" / ");
    },
    setTarget(key) {
      this.targetKey = key;
    },
    // Vue 2 cannot observe an array index assignment, so v-model into
    // form[key][idx] would leave `payload` stale and the save button dead.
    setValue(key, idx, value) {
      this.$set(this.form[key], idx, value);
    },
    addValue(key) {
      this.form[key].push("");
    },
    removeValue(key, idx) {
      this.form[key].splice(idx, 1);
      if (!this.form[key].length) this.form[key].push("");
    },
    // Files are uploaded as soon as they are picked, so the save only has to
    // send fids. Nothing is attached to the node until that save, so a picked
    // file on a cancelled form is just an unreferenced upload.
    async onFilesPicked(event) {
      const picked = event.target.files;
      if (!picked || !picked.length) return;
      this.fileError = "";
      this.uploadingFiles = true;
      try {
        const data = await this.projectStore.uploadPermitApplicationFiles(picked);
        // Appended, not replaced: the input is emptied after every batch so
        // documents can be added a few at a time.
        // $set, not a plain assign: on an instance whose data predates this
        // field (a hot reload with the form open) `files` is not reactive yet.
        this.$set(this.form, "files", [...this.attachedFiles, ...(data.files || [])]);
        if (data.errors && data.errors.length) {
          this.fileError = data.errors.join(" · ");
        }
      } catch (err) {
        console.error("PermitApplicationForm onFilesPicked error:", err);
        this.fileError = err.message || "Could not upload the planning documents.";
      } finally {
        this.uploadingFiles = false;
        // Without this, re-picking the same file fires no change event.
        if (this.$refs.fileInput) this.$refs.fileInput.value = "";
      }
    },
    // Drops the attachment from this application on the next save. The managed
    // file is left alone — another node may reference it.
    removeFile(idx) {
      this.$set(this.form, "files", this.attachedFiles.filter((file, i) => i !== idx));
    },
    // Shared with the Permit tab's CBA Status column — both render the global
    // .pt-cba chip, so both take the modifier straight from the utility.
    cbaClass(status) {
      return cbaStatusClass(status);
    },
    // A trailing blank row is not a value.
    cleanValues(key) {
      return (this.form[key] || [])
        .map((value) => String(value == null ? "" : value).trim())
        .filter((value) => value !== "");
    },
    // Stored deltas for a multi-value field. `multi` is the full set; the older
    // scalar key is the fallback when the backend predates it.
    loadedValues(key) {
      if (!this.loaded) return [];
      const multi = this.loaded.multi && this.loaded.multi[key];
      if (Array.isArray(multi)) return multi.map((value) => String(value).trim()).filter(Boolean);
      const single = this.loaded[key];
      return single ? [String(single).trim()] : [];
    },
    // A node ID addresses one node, so it loads straight away. Anything else is
    // a search: one hit loads, several are listed to choose from.
    async loadApplication() {
      const reference = this.reference.trim();
      if (!reference || this.loading) return;
      if (/^\d+$/.test(reference)) {
        return this.selectMatch(reference);
      }
      this.loading = true;
      this.resetResult();
      try {
        const data = await this.projectStore.searchPermitApplications(reference);
        this.moreMatches = Boolean(data.more);
        if (!data.results.length) {
          this.error = `Nothing matches "${reference}".`;
        } else if (data.results.length === 1) {
          await this.load(data.results[0].nid);
        } else {
          this.matches = data.results;
        }
      } catch (err) {
        console.error("PermitApplicationForm loadApplication error:", err);
        this.error = err.message || "Could not search permit applications.";
      } finally {
        this.loading = false;
      }
    },
    async selectMatch(nid) {
      if (this.loading) return;
      this.loading = true;
      this.resetResult();
      try {
        await this.load(nid);
      } finally {
        this.loading = false;
      }
    },
    async load(nid) {
      try {
        const data = await this.projectStore.fetchPermitApplication(nid);
        this.matches = [];
        this.applyLoaded(data);
      } catch (err) {
        console.error("PermitApplicationForm load error:", err);
        this.error = err.message || "Could not load that permit application.";
      }
    },
    resetResult() {
      // stageLabels is vocabulary state, not per-node state — keep it. The
      // rows belong to the node, so they go and a blank one takes their place.
      this.error = null;
      this.stageError = "";
      this.fileError = "";
      this.notice = "";
      this.warnings = [];
      this.matches = [];
      this.moreMatches = false;
      this.loaded = null;
      this.form = emptyForm();
      this.stageRows = [];
      this.ensureBlankRow();
    },
    // Prefill from the stored values so every box shows what is actually on the
    // node — the diff in `payload` is taken against this same snapshot.
    applyLoaded(data) {
      this.loaded = data;
      const form = {
        title: data.title || "",
        applicationDate: data.field_applicateion_date_iso || "",
        // Copied, not referenced: removing a row here must not mutate the
        // snapshot `payload` diffs against.
        files: (data.field_planning_documents_type_co || []).map((file) => ({ ...file })),
      };
      MULTI_FIELDS.forEach(({ key }) => {
        const values = this.loadedValues(key);
        form[key] = values.length ? [...values] : [""];
      });
      this.form = form;
      // Label the stored leaves with their breadcrumb, so a stage the cascade
      // has not walked to still reads properly. The static tree is the source;
      // whatever the backend resolved covers a term missing from it.
      const labels = {};
      this.storedStages.forEach((stage) => {
        labels[stage.tid] =
          stageBreadcrumb(stage.tid) || stage.label || stage.name || `Stage ${stage.tid}`;
      });
      this.stageLabels = { ...this.stageLabels, ...labels };
      // One editable row per stored value, rebuilt from the static tree: the
      // path down to the stored leaf, with the options at every step.
      this.stageRows = this.storedStages
        .map((stage) => stageLevelsFor(stage.tid))
        .filter(Boolean)
        .map((levels) => ({ levels }));
      // A stage the static tree does not carry cannot be rendered as a row, and
      // dropping it silently would wipe it on the next save. Say so instead.
      const unrenderable = this.storedStages.length - this.stageRows.length;
      if (unrenderable > 0) {
        this.stageError =
          `${unrenderable} stored stage(s) are not in the static stage list, so they are not ` +
          `shown here. Saving this form replaces the stored stages with the rows above — edit ` +
          `the node in Drupal instead if those values have to be kept.`;
      }
      this.ensureBlankRow();
    },
    async save() {
      if (!this.canSave) return;
      this.saving = true;
      this.error = null;
      this.notice = "";
      this.warnings = [];
      try {
        let data;
        if (this.isCreate) {
          const request = {
            pid: this.presaleId,
            placement: this.targetPlacement,
            ...this.payload,
          };
          // Omitted rather than null: the endpoint reads a missing fid as
          // "the project's first PTC" and a missing uid as that PTC's first
          // UTC, which a null would also do, but sending the chosen one is the
          // whole point of the selector.
          if (this.targetFid) request.fid = this.targetFid;
          if (this.targetUid) request.uid = this.targetUid;
          data = await this.projectStore.savePermitApplication(request);
          this.notice = data.matched_existing
            ? `That permit number was already on this project — node ${data.nid} updated.`
            : `Created node ${data.nid}${data.attached ? " and added it to the project." : "."}`;
          // Carry on editing what was just created, so a follow-up save writes
          // to that node instead of adding a second milestone.
          this.mode = "edit";
        } else {
          data = await this.projectStore.editPermitApplication(this.loaded.nid, this.payload);
          this.notice = `Updated ${(data.updated || []).join(", ") || "nothing"}.`;
        }
        this.warnings = data.warnings || [];
        // Repaint from what was actually stored — a date sent as ISO comes back
        // as the stored timestamp, a stage as its resolved term.
        if (data.node) this.applyLoaded(data.node);
        this.$emit("saved", data);
      } catch (err) {
        console.error("PermitApplicationForm save error:", err);
        this.error = err.message || "Could not save the permit application.";
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.pa-form {
  display: flex;
  flex-direction: column;
  max-height: 88vh;
  color: var(--rpd-ink);
  font-family: var(--rpd-font-sans);
}

.pa-modes {
  display: flex;
  gap: 8px;
}

.pa-required {
  color: var(--rpd-coral-dark);
}

.pa-stage-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}

.pa-cascade-select {
  flex: 1 1 160px;
  min-width: 0;
}

.pa-stage-flag {
  flex-shrink: 0;
  padding: 2px 8px;
  border-radius: var(--rpd-r-pill);
  background: var(--rpd-bg-2);
  color: var(--rpd-coral-dark);
  font-size: 11px;
  font-weight: 600;
}

.pa-stage-drop {
  width: 20px;
  height: 20px;
  border: none;
  background: transparent;
  font-size: 11px;
}

.pa-multi-item {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-bottom: 6px;
}

.pa-multi-item .pa-input {
  flex: 1;
  min-width: 0;
}

.pa-multi-remove {
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border: 1px solid var(--rpd-line);
  border-radius: var(--rpd-r-sm);
  background: var(--rpd-white);
  color: var(--rpd-muted);
  cursor: pointer;
}

.pa-multi-remove:hover:not(:disabled) {
  color: var(--rpd-coral-dark);
  border-color: var(--rpd-coral);
}

.pa-multi-add {
  align-self: flex-start;
  padding: 5px 10px;
  border: 1px dashed var(--rpd-line);
  border-radius: var(--rpd-r-sm);
  background: transparent;
  color: var(--rpd-muted);
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.pa-multi-add:hover:not(:disabled) {
  color: var(--rpd-ink-2);
  border-color: var(--rpd-navy-300);
}

.pa-multi-remove:disabled,
.pa-multi-add:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.pa-help--warn {
  color: var(--rpd-coral-dark);
}

.pa-mode {
  flex: 1;
  padding: 8px 10px;
  border: 1px solid var(--rpd-line);
  border-radius: var(--rpd-r-sm);
  background: var(--rpd-white);
  color: var(--rpd-ink-2);
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.pa-mode:hover:not(:disabled) {
  background: var(--rpd-bg-2);
}

.pa-mode--active {
  border-color: var(--rpd-navy-700);
  background: var(--rpd-navy-700);
  color: var(--rpd-white);
}

.pa-mode:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

/* The level step's two choices. Same shape as .pa-mode, but stacked and with
   room for the sentence that says what each level means. */
.pa-level {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--rpd-line);
  border-radius: var(--rpd-r-sm);
  background: var(--rpd-white);
  color: var(--rpd-ink-2);
  font-family: inherit;
  text-align: left;
  cursor: pointer;
}

.pa-level + .pa-level {
  margin-top: 8px;
}

.pa-level:hover:not(:disabled):not(.pa-level--static) {
  background: var(--rpd-bg-2);
}

.pa-level--static {
  cursor: default;
}

.pa-level--active {
  border-color: var(--rpd-navy-700);
  background: var(--rpd-navy-700);
  color: var(--rpd-white);
}

.pa-level:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.pa-level-title {
  font-size: 13px;
  font-weight: 600;
}

.pa-level-note {
  font-size: 12px;
  line-height: 1.45;
  color: var(--rpd-muted);
}

.pa-level--active .pa-level-note {
  color: rgba(255, 255, 255, 0.78);
}

.pa-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid var(--rpd-line);
}

.pa-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.pa-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  border-radius: var(--rpd-r-sm);
  background: var(--rpd-bg-2);
  color: var(--rpd-navy-600);
}

.pa-title {
  font-size: 15px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pa-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  padding: 0;
  border: none;
  border-radius: var(--rpd-r-sm);
  background: transparent;
  color: var(--rpd-muted);
  cursor: pointer;
}

.pa-close:hover:not(:disabled) {
  background: var(--rpd-bg-2);
  color: var(--rpd-ink);
}

.pa-body {
  padding: 16px 20px 20px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 14px;
  flex: 1;
  min-height: 0;
}

.pa-hint,
.pa-help {
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
  color: var(--rpd-muted);
}

.pa-row {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.pa-row--pair {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.pa-row--pair > div {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.pa-label {
  margin: 0;
  font-size: 12px;
  font-weight: 600;
}

/* A heading inside a row that already has one — the read-only CBA summary
   under the stage picker. */
.pa-label--sub {
  margin-top: 4px;
  color: var(--rpd-muted);
}

/* The chips themselves are the global .pt-cba rules in
   assets/styles/permit-table.css, so they read the same here as in the Permit
   tab's CBA Status column, dark mode included. Only the layout is local. */
.pa-cba-list {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.pa-cba-item {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 8px;
}

.pa-cba-stage {
  flex: 1 1 160px;
  min-width: 0;
  font-size: 12px;
  color: var(--rpd-ink-2);
}

/* Wraps rather than overflowing: a stage can carry all eight statuses, and
   the form is narrow on a phone. */
.pa-cba-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  min-width: 0;
}

/* The global chip spaces itself with margin-left, which would double up on
   the gap above and leave wrapped rows flush. */
.pa-cba-chips .pt-cba {
  margin-left: 0;
}

.pa-input {
  width: 100%;
  min-width: 0;
  padding: 8px 10px;
  border: 1px solid var(--rpd-line);
  border-radius: var(--rpd-r-sm);
  background: var(--rpd-white);
  color: var(--rpd-ink);
  font-size: 13px;
  font-family: inherit;
}

.pa-textarea {
  resize: vertical;
  line-height: 1.5;
}

.pa-input:focus {
  outline: none;
  border-color: var(--rpd-navy-400);
}

.pa-input:disabled {
  background: var(--rpd-bg-2);
  color: var(--rpd-muted);
  cursor: not-allowed;
}

.pa-load {
  display: flex;
  gap: 8px;
}

.pa-load-btn,
.pa-cancel-btn,
.pa-save-btn {
  padding: 8px 14px;
  border-radius: var(--rpd-r-sm);
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  white-space: nowrap;
  cursor: pointer;
}

.pa-load-btn,
.pa-cancel-btn {
  border: 1px solid var(--rpd-line);
  background: var(--rpd-white);
  color: var(--rpd-ink-2);
}

.pa-load-btn:hover:not(:disabled),
.pa-cancel-btn:hover:not(:disabled) {
  background: var(--rpd-bg-2);
}

.pa-save-btn {
  border: 1px solid var(--rpd-navy-700);
  background: var(--rpd-navy-700);
  color: var(--rpd-white);
}

.pa-save-btn:hover:not(:disabled) {
  background: var(--rpd-navy-800);
}

.pa-load-btn:disabled,
.pa-cancel-btn:disabled,
.pa-save-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.pa-current {
  padding: 12px 14px;
  border: 1px solid var(--rpd-line-2);
  border-radius: var(--rpd-r-md);
  background: var(--rpd-bg);
}

.pa-current-title {
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 700;
}

.pa-current-grid {
  display: grid;
  grid-template-columns: 96px 1fr;
  gap: 4px 10px;
  margin: 0;
  font-size: 12px;
}

.pa-current-grid dt {
  color: var(--rpd-muted);
  font-weight: 500;
}

.pa-current-grid dd {
  margin: 0;
  word-break: break-word;
}

/* The stage vocabulary is city-based and long, so the picker scrolls inside its
   own box instead of stretching the modal. */
.pa-picker {
  max-height: 190px;
  overflow-y: auto;
  border: 1px solid var(--rpd-line);
  border-radius: var(--rpd-r-sm);
  background: var(--rpd-white);
}

.pa-picker-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0;
  padding: 5px 10px 5px 8px;
  font-size: 12px;
  line-height: 1.5;
  cursor: pointer;
}

.pa-picker-item:hover {
  background: var(--rpd-bg-2);
}

.pa-picker-empty {
  padding: 8px 10px;
}

.pa-checkbox {
  width: 15px;
  height: 15px;
  margin-top: 2px;
  flex-shrink: 0;
  accent-color: var(--rpd-navy-700);
  cursor: pointer;
}

.pa-matches {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;
  max-height: 210px;
  overflow-y: auto;
}

.pa-match {
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
  padding: 8px 10px;
  border: 1px solid var(--rpd-line);
  border-radius: var(--rpd-r-sm);
  background: var(--rpd-white);
  color: var(--rpd-ink);
  text-align: left;
  font-family: inherit;
  cursor: pointer;
}

.pa-match:hover:not(:disabled) {
  background: var(--rpd-bg-2);
  border-color: var(--rpd-navy-300);
}

.pa-match:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.pa-match-title {
  font-size: 13px;
  font-weight: 600;
}

.pa-match-meta {
  font-size: 11.5px;
  line-height: 1.45;
  color: var(--rpd-muted);
  word-break: break-word;
}

.pa-files {
  margin: 0 0 8px;
  padding-left: 0;
  list-style: none;
  font-size: 12px;
  line-height: 1.6;
  word-break: break-word;
}

.pa-files li {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
}

.pa-files li > a,
.pa-files li > span {
  flex: 1;
  min-width: 0;
}

/* A label wrapping a hidden input, so the file picker gets the same dashed
   "add" affordance as the other unlimited-cardinality fields. */
.pa-file-add {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.pa-file-add--busy {
  opacity: 0.55;
  cursor: not-allowed;
}

.pa-file-input {
  position: absolute;
  inset: 0;
  width: 100%;
  opacity: 0;
  cursor: pointer;
}

.pa-file-add--busy .pa-file-input {
  cursor: not-allowed;
}

.pa-alert {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  border-radius: var(--rpd-r-md);
  font-size: 12px;
  line-height: 1.5;
}

.pa-alert-title {
  font-weight: 700;
}

.pa-alert--error {
  border: 1px solid var(--rpd-coral);
  background: rgba(242, 106, 111, 0.1);
  color: var(--rpd-coral-dark);
}

.pa-alert--warn {
  border: 1px solid var(--rpd-amber);
  background: rgba(244, 183, 64, 0.12);
  color: var(--rpd-ink-2);
}

.pa-alert--ok {
  border: 1px solid var(--rpd-line);
  background: var(--rpd-bg-2);
  color: var(--rpd-ink-2);
}

.pa-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 20px;
  border-top: 1px solid var(--rpd-line);
  background: var(--rpd-bg);
}

.pa-footer-status {
  font-size: 12px;
  color: var(--rpd-muted);
}

.pa-footer-actions {
  display: flex;
  gap: 8px;
}

/* Narrow screens: the paired fields and the footer both stack rather than
   squeezing, and the action buttons go full width for thumb reach. */
@media (max-width: 575px) {
  .pa-row--pair {
    grid-template-columns: 1fr;
  }

  .pa-footer {
    flex-direction: column;
    align-items: stretch;
  }

  .pa-footer-actions {
    flex-direction: column-reverse;
  }

  .pa-load {
    flex-direction: column;
  }
}

html.dark .pa-form {
  color: #e5e7eb;
}

html.dark .pa-header,
html.dark .pa-footer {
  border-color: #2b3441;
}

html.dark .pa-footer {
  background: #161c25;
}

html.dark .pa-icon,
html.dark .pa-close:hover:not(:disabled) {
  background: #232c38;
  color: #e5e7eb;
}

html.dark .pa-hint,
html.dark .pa-help,
html.dark .pa-footer-status,
html.dark .pa-close,
html.dark .pa-label--sub,
html.dark .pa-current-grid dt {
  color: #9aa4b2;
}

html.dark .pa-cba-stage {
  color: #e5e7eb;
}

html.dark .pa-input {
  background: #1b222c;
  border-color: #2b3441;
  color: #e5e7eb;
}

html.dark .pa-input:focus {
  border-color: var(--rpd-navy-300);
}

html.dark .pa-input:disabled {
  background: #232c38;
  color: #9aa4b2;
}

html.dark .pa-current,
html.dark .pa-picker,
html.dark .pa-match {
  background: #1b222c;
  border-color: #2b3441;
}

html.dark .pa-picker-item:hover,
html.dark .pa-match:hover:not(:disabled) {
  background: #232c38;
}

html.dark .pa-match {
  color: #e5e7eb;
}

html.dark .pa-match-meta {
  color: #9aa4b2;
}

html.dark .pa-stage-flag {
  background: #232c38;
}

html.dark .pa-multi-remove {
  background: #232c38;
  border-color: #2b3441;
  color: #9aa4b2;
}

html.dark .pa-multi-add {
  border-color: #2b3441;
  color: #9aa4b2;
}

html.dark .pa-multi-add:hover:not(:disabled) {
  color: #e5e7eb;
}

html.dark .pa-level {
  background: #232c38;
  border-color: #2b3441;
  color: #e5e7eb;
}

html.dark .pa-level:hover:not(:disabled) {
  background: #2b3441;
}

html.dark .pa-level-note {
  color: #9aa4b2;
}

html.dark .pa-level--active {
  background: var(--rpd-navy-700);
  border-color: var(--rpd-navy-600);
  color: var(--rpd-white);
}

html.dark .pa-level--active .pa-level-note {
  color: rgba(255, 255, 255, 0.78);
}

html.dark .pa-mode {
  background: #232c38;
  border-color: #2b3441;
  color: #e5e7eb;
}

html.dark .pa-mode:hover:not(:disabled) {
  background: #2b3441;
}

html.dark .pa-mode--active {
  background: var(--rpd-navy-700);
  border-color: var(--rpd-navy-600);
  color: var(--rpd-white);
}

html.dark .pa-load-btn,
html.dark .pa-cancel-btn {
  background: #232c38;
  border-color: #2b3441;
  color: #e5e7eb;
}

html.dark .pa-load-btn:hover:not(:disabled),
html.dark .pa-cancel-btn:hover:not(:disabled) {
  background: #2b3441;
}

html.dark .pa-alert--warn {
  color: #f2d9a3;
}

html.dark .pa-alert--ok {
  background: #232c38;
  border-color: #2b3441;
  color: #e5e7eb;
}
</style>
